import Path from 'webmango-utils/path'
import { Errors } from 'webmango-utils'
import pathLib from 'path'
import { createSelector } from 'reselect'
import matches from 'lodash/matches'
import pickBy from 'lodash/pickBy'
import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'

import { isDatasetPath } from '../lib/file'
import { getAll as getAllProcesses } from './process'

const ORDER = [ 'group', 'project', 'sample', 'dataset' ]

export const SEPARATOR = '|'
export const SUB_SEPARATOR = ':'
export const LOCAL_GROUP = 'local'

export const canHaveRemote = (id) => {
  const { group } = fromIDBasic(id, 'group')
  return (
    group !== LOCAL_GROUP
  )
}

const DEFAULT_ROOT = {
  w09: 'repository'
}

const URL_ROOT = '/-/k'

function getDefaultRoot (group) {
  return DEFAULT_ROOT[group] || ''
}

/**
 * ID Handling
 */

export function toIDMaybe (data, type) {
  try {
    return toID(data, type)
  } catch (err) {
    if ((err.type === MissingValueError)) {
      return // ignore
    }
    throw err
  }
}

export function toIDsMaybe (data) {
  return clean({
    groupID: toIDMaybe(data, 'group'),
    projectID: toIDMaybe(data, 'project'),
    sampleID: toIDMaybe(data, 'sample'),
    datasetID: toIDMaybe(data, 'dataset')
  })
}

export function toID (key, type) {
  assertData(key, type)
  const { group, project, sample, dataset } = key
  const parts = [ group, project, sample, dataset ].filter(Boolean)
  const id = parts.slice(0, ORDER.indexOf(type) + 1).join(SEPARATOR)
  assertID(id, type)
  return id
}

export function fromData (data, type) {
  return fromID(toID(data, type), type)
}

export function fromIDBasic (id, type) {
  assertID(id, type)
  const index = ORDER.indexOf(type)
  const parts = id.split(SEPARATOR).slice(0, index + 1)
  const [ group, project, sample, dataset ] = parts
  let name = parts[index]
  name = name.split(SUB_SEPARATOR).pop()
  const parentID = getParentID(id)
  return toIDKeys(clean({
    id,
    type,
    name,
    group,
    project,
    sample,
    parentID,
    parentType: getIDType(parentID),
    dataset
  }))
}

export function toIDKeys (key = {}) {
  const { group, project, sample, dataset } = key
  if (!group) return key
  const obj = { ...key }
  if (group) obj.groupID = toID(key, 'group')
  if (project) obj.projectID = toID(key, 'project')
  if (sample) obj.sampleID = toID(key, 'sample')
  if (dataset) obj.datasetID = toID(key, 'dataset')

  return obj
}

function getIDType (id) {
  if (!id) return
  const parts = id.split(SEPARATOR)
  return ORDER[parts.length - 1]
}

function getParentID (id) {
  if (!id) return
  assertIDComponents(id)
  const parts = id.split(SEPARATOR)
  const last = parts[parts.length - 1]
  const lastParts = last.split(SUB_SEPARATOR)
  if (lastParts.length > 1) {
    const subParent = lastParts.slice(0, -1).join(SUB_SEPARATOR)
    return parts.slice(0, -1).concat(subParent).join(SEPARATOR)
  }
  return parts.slice(0, -1).join(SEPARATOR)
}

export function findParentOfType (id, type) {
  assertID(id, type)
  const parts = id.split(SEPARATOR)
  if (parts.length < ORDER.indexOf(type) + 1) {
    throw Errors(422, `Cannot get ${type} ID from type ${getIDType(id)}: ${id}`)
  }

  return parts.slice(0, ORDER.indexOf(type) + 1).join(SEPARATOR)
}

export const getParentIDs = (id, ids = []) => {
  const parentID = getParentID(id)
  if (!parentID) return ids
  ids.push(parentID)
  return getParentIDs(parentID, ids)
}

export function fromID (id, type) {
  const data = fromIDBasic(id, type)
  const urls = getURLs(id, type)
  const paths = getPaths(id, type)

  return clean({
    ...data,
    ...urls,
    ...paths
  })
}

function getPaths (id, type) {
  const { group, project, dataset, sample } = fromIDBasic(id, type)
  let localPath
  let remotePath
  let localSamplePath
  let remoteSamplePath
  let localSampleDataPath
  let localSampleWorkPath
  let remoteProjectPath
  let localProjectPath

  if (project) {
    localProjectPath = getLocalProjectPath({ group, project })
    remoteProjectPath = getRemoteProjectPath({ group, project })
  }

  if (sample && dataset) {
    localPath = getLocalSamplePath({ group, project, sample }, 'data', Path.basename(dataset, '.bz2'))
    remotePath = getRemoteSamplePath({ group, project, sample }, dataset)
  }

  if (sample) {
    localSamplePath = getLocalSamplePath({ group, project, sample })
    remoteSamplePath = getRemoteSamplePath({ group, project, sample })
    localSampleDataPath = localJoin(localSamplePath, 'data')
    localSampleWorkPath = localJoin(localSamplePath, 'work')
  }

  return clean({
    localPath,
    remotePath,
    localSamplePath,
    remoteSamplePath,
    localSampleDataPath,
    localSampleWorkPath,
    remoteProjectPath,
    localProjectPath
  })
}

function joinURLParts (...parts) {
  return [URL_ROOT, ...parts.map(p => encodeURIComponent(p))].join('/')
}

function getURLs (id, type) {
  const { group, project, sample, dataset } = fromIDBasic(id, type)

  return clean(Object.assign({}, {
    get groupURL () {
      return joinURLParts(group)
    },
    get projectURL () {
      if (!project) return
      return joinURLParts(group, project)
    },
    get sampleURL () {
      if (!sample) return
      return joinURLParts(group, project, sample)
    },
    get datasetURL () {
      if (!dataset) return
      return joinURLParts(group, project, sample, dataset)
    },
    get url () {
      return this[`${type}URL`]
    }
  }))
}

function getLocalProjectPath ({ group, project }, ...args) {
  return localJoin(group, project)
}

function getRemoteProjectPath ({ group, project }, ...args) {
  return remoteJoin(group, project)
}

function getLocalSamplePath ({ group, project, sample }, ...args) {
  sample = sample.split(SUB_SEPARATOR).join('/')
  return localJoin(group, project, sample, ...args)
}

function getRemoteSamplePath ({ group, project, sample }, ...args) {
  if (group === LOCAL_GROUP) return ''
  const root = getDefaultRoot(group)
  sample = sample.split(SUB_SEPARATOR).join('/')
  return remoteJoin(root, project, sample, ...args)
}

export function localJoin (...args) {
  return Path.join('/', ...args)
}

export function remoteJoin (...args) {
  return pathLib.join(...args)
}

/**
 * File Handling
 */

export function pathToSampleID (path) {
  const [ group, project, ...sample ] = path.split('/').filter(Boolean)
  if (!(group && project && sample.length)) return
  try {
    return toID({
      group,
      project,
      sample: sample.join(SUB_SEPARATOR)
    }, 'sample')
  } catch (err) {
    // ignore err
  }
}

export function canArchive (filepath) {
  return (
    isDatasetPath(filepath) &&
    canHaveRemote(pathToSampleID(filepath))
  )
}

export function pathToDataset (path) {
  if (!isDatasetPath(path)) return
  const sampleID = pathToSampleID(path)
  const sampleKey = fromID(sampleID, 'sample')
  return fromID(toID({
    ...sampleKey,
    dataset: Path.basename(path)
  }, 'dataset'), 'dataset')
}

function filesToDatasetIDs (sampleID, files = []) {
  assertID(sampleID, 'sample')
  const sampleKey = fromID(sampleID, 'sample')
  return files
    .filter(f => isDatasetPath(f.name))
    .map(file => toID({ ...sampleKey, dataset: file.name }, 'dataset'))
}

function filesToDatasets (sampleID, files = []) {
  return filesToDatasetIDs(sampleID, files).map(id => fromID(id, 'dataset'))
}

function makeChildID (parentID, child) {
  const id = parentID.split(SEPARATOR).concat(child).join(SEPARATOR)
  assertIDComponents(id)
  return id
}

function makeSubChildID (parentID, child) {
  const parts = parentID.split(SEPARATOR)
  const p = parts.pop().split(SUB_SEPARATOR).concat(child).join(SUB_SEPARATOR)
  const id = parts.concat(p).join(SEPARATOR)
  assertIDComponents(id)
  return id
}

export function createSampleIDForProject (projectID, name) {
  const parts = name.split('/')
  const id = makeChildID(projectID, parts.join(SUB_SEPARATOR))
  assertID(id, 'sample')
  return id
}

export function filesToDatasetMap (sampleID, files = [], key) {
  assertID(sampleID, 'sample')

  return files
    .filter(f => isDatasetPath(f.name))
    .reduce((o, file) => {
      const id = makeChildID(sampleID, file.name)
      return Object.assign(o, {
        [id]: file
      })
    }, {})
}

export function filesToSubsampleIDs (sampleID, files = []) {
  const parent = fromID(sampleID, 'sample')
  return files
    .filter(f => {
      return (
        f.isDir &&
        !f.name.endsWith('_nc') &&
        f.name !== 'work' &&
        f.name !== 'data' &&
        f.path !== parent.localSamplePath &&
        f.path !== parent.remoteSamplePath
      )
    })
    .map(file => makeSubChildID(sampleID, file.name))
}

export function filesToSubsamples (sampleID, files) {
  return filesToSubsampleIDs(sampleID, files).map(id => fromID(id, 'sample'))
}

/**
 * Dataset Selectors
 */

const getLocalDatasetsMap = state => state.datasets

export const getLocalDatasetData = createSelector([ getLocalDatasetsMap ], (datasets) => {
  return Object.entries(datasets).map(([key, value]) => ({
    id: key,
    ...value
  }))
})

export const getLocalDatasetIDs = createSelector([ getLocalDatasetsMap ], (localDatasets) => {
  return Object.keys(localDatasets)
})

const getFiles = state => state.files
const getDirs = createSelector([ getFiles ], files => {
  return Object.entries(files || {}).filter(([ path, files ]) => {
    return files && files.length
  }).map(([ path ]) => path).sort()
})

const getSampleDirs = createSelector([ getDirs ], dirs => {
  const sampleDirs = Array.from(new Set(dirs
    .filter(dir => dir.endsWith('/data') || dir.endsWith('/work'))
    .map(dir => Path.dirname(dir))))
  return sampleDirs.filter(dir => pathToSampleID(dir))
})

function allSampleIDsFromID (id, sampleIDs = new Set()) {
  const parentID = getParentID(id)
  if (getIDType(parentID) !== 'sample') return Array.from(sampleIDs)
  sampleIDs.add(parentID)
  return allSampleIDsFromID(parentID, sampleIDs)
}

export const getIsTemporary = createSelector([
  getFiles,
  (state, { sampleID }) => sampleID
], (files, sampleID) => {
  if (!sampleID) return
  const { localSampleDataPath } = fromID(sampleID, 'sample')
  return !files[localSampleDataPath]
})

export const getSampleIDs = createSelector([
  getSampleDirs,
  ({ current }) => current.sampleID
], (sampleDirs, sampleID) => {
  const sampleIDs = new Set([
    ...sampleDirs.map(dir => pathToSampleID(dir))
  ])

  if (sampleID && getIDType(sampleID) === 'sample') sampleIDs.add(sampleID)
  sampleIDs.forEach(id => {
    const allIDs = allSampleIDsFromID(id)
    allIDs.forEach(id => sampleIDs.add(id))
  })
  return Array.from(sampleIDs).sort()
})

export const getSamples = createSelector([
  getSampleIDs
], (sampleIDs) => {
  return sampleIDs.map(id => fromID(id, 'sample'))
})

export const getProjectIDs = createSelector([ getSamples ], (samples) => {
  return Array.from(new Set(samples.map(({ projectID }) => projectID)))
})

export const getProjects = createSelector([ getProjectIDs ], (projectIDs) => {
  return projectIDs.map(id => fromID(id, 'project'))
})

export const getGroupedProjects = createSelector([ getProjects ], (projects) => {
  projects = sortBy(projects.slice(), ['group', 'project'])
  const grouped = groupBy(projects, 'group')
  return Object.assign({
    // sort local first in object
    local: grouped.local || []
  }, grouped)
})

export const getSamplesForProject = createSelector([
  getSamples,
  (state, { projectID }) => projectID
], (samples, projectID) => {
  return samples.filter(matches({ projectID }))
})

export const getRootSamplesForProject = createSelector([
  getSamplesForProject
], (samples) => {
  return samples.filter(s => s.parentType !== 'sample')
})

export const getRootSampleIDsForProject = createSelector([
  getRootSamplesForProject
], (samples) => {
  return samples.map(({ id }) => id)
})

export const getProcessesForProject = createSelector([
  getAllProcesses,
  (state, { projectID }) => projectID
], (processes, projectID) => {
  return processes.filter(matches({ projectID }))
})

export const getProcessesForSample = createSelector([
  getAllProcesses,
  (state, { sampleID }) => sampleID
], (processes, sampleID) => {
  return processes.filter(matches({ sampleID }))
})

export const getProjectID = createSelector([
  getProjectIDs,
  (state, { projectID }) => projectID
], (projectIDs, projectID) => {
  return projectIDs.find(id => id === projectID)
})

export const getProject = createSelector([
  getProjectID
], (projectID) => {
  if (!projectID) return
  return fromID(projectID, 'project')
})

export const getSample = createSelector([
  getSamples,
  (state, { sampleID }) => sampleID
], (samples, sampleID) => {
  const s = samples.find(matches({ id: sampleID }))
  if (!s) return Object.assign({ isTemporary: true }, fromID(sampleID, 'sample'))
  return s
})

export const getDataset = createSelector([
  (state, { datasetID }) => datasetID
], (datasetID) => {
  return fromID(datasetID, 'dataset')
})

export const getDatasetsForSample = createSelector([
  getFiles,
  getSample
], (files, sample) => {
  const dataFiles = files[sample.localSampleDataPath]
  return filesToDatasets(sample.id, dataFiles)
})

export const getSubsamplesForSample = createSelector([
  getSamples,
  (state, { sampleID }) => sampleID
], (samples, sampleID) => {
  return samples.filter(matches({ parentID: sampleID }))
})

export const getRemoteDatasetsForSample = createSelector([
  (state, { sampleID }) => sampleID,
  ({ remoteFiles }, { sampleID }) => remoteFiles[sampleID]
], (sampleID, files = []) => {
  return filesToDatasets(sampleID, files)
})

export const getFilesForDataset = createSelector([
  ({ remoteFiles }) => remoteFiles,
  ({ files }) => files,
  (state, { datasetID }) => datasetID
], (remoteFiles, files, datasetID) => {
  const { localPath, remotePath, localSampleDataPath, sampleID } = fromID(datasetID, 'dataset')
  const remoteFile = (remoteFiles[sampleID] || [])
    .find(matches({ path: remotePath }))
  const localFile = (files[localSampleDataPath] || [])
    .find(matches({ path: localPath }))
  return { localFile, remoteFile }
})

export const getRemoteSubsamplesForSample = createSelector([
  (state, { sampleID }) => sampleID,
  ({ remoteFiles }, { sampleID }) => remoteFiles[sampleID]
], (sampleID, files = []) => {
  return filesToSubsamples(sampleID, files)
})

/**
 * Utils
 */

function clean (obj) {
  return pickBy(obj, v => v != null)
}

class MissingValueError extends Error {
  constructor ({ message = '', name, type, object } = {}) {
    message = `Invalid Object: missing ${name} from ${type}. ${message}`
    super(message)
    if (typeof this.constructor.captureStackTrace === 'function') this.constructor.captureStackTrace(this, MissingValueError.name)
    Object.assign(this, {
      type: MissingValueError,
      name: MissingValueError.name,
      constructor: MissingValueError,
      object
    })
  }
}

function matchBadID (id = '') {
  const BAD_ID_REGEX = /[^a-z0-9%\-_.]/i
  return BAD_ID_REGEX.exec(String(id))
}

export function assertSimpleID (id) {
  if (!id || !id.length) throw Errors(422, 'id required')
  const badMatch = matchBadID(id)
  if (badMatch) {
    throw Errors(422, `Invalid character: '${badMatch[0]}'`, { value: id })
  }
}

export function assertIDComponent (id) {
  if (!id || !id.length) throw Errors(422, 'id component required')
  const badMatch = matchBadID(id)
  if (badMatch) {
    if (badMatch[0] === SUB_SEPARATOR) return
    throw Errors(422, `Invalid character: '${badMatch[0]}'`, { value: id })
  }
}

export function assertIDComponents (id) {
  if (!id) throw Errors(422, 'assertIDComponents – id required')
  const segments = id.split(SEPARATOR)
  segments.forEach(value => assertIDComponent(value))
}

export function assertID (id, type) {
  if (!id) throw Errors(422, 'assertID – id required')
  if (!type) throw Errors(422, 'assertID – type required')
  const segments = id.split(SEPARATOR)
  ORDER.slice(0, ORDER.indexOf(type) + 1).forEach((name, index) => {
    if (!segments[index]) throw Errors(422, `assertID – Invalid ID: missing ${name} from ${type} ID: ${id}`)
    assertIDComponent(segments[index])
  })
}

function assertData (data, type) {
  if (!data) throw Errors(422, 'assertData – data required')
  if (!type) throw Errors(422, 'assertData – type required')
  ORDER.slice(0, ORDER.indexOf(type) + 1).forEach(name => {
    if (data[name]) return
    throw new MissingValueError({ object: data, type, name })
  })
}



// WEBPACK FOOTER //
// ../webmango-core/selectors/dataset.js